import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { IdentityService } from '../../../../core/authentication/services/identity.service';
import { UserData } from '../../models/user-data';
import { select, Store } from '@ngrx/store';
import { ChangePasswordDto } from '../../models/change-password-dto';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StateService } from '@uirouter/core';
import { IMAGE_UPLOAD_ACCEPT, imageUploadErrorMessage } from 'account-hybrid/common/utils/image-upload-error.util';
import { AuthenticationState } from 'account-hybrid/features/authentication/store/authentication/authentication.state';
import {
    getChangePasswordError,
    getChangeUserError,
    getUploadProfilePictureError,
    getUserData,
    isChangePasswordLoading,
    isChangeUserLoading,
    isUploadProfilePictureLoading,
} from 'account-hybrid/features/authentication/store/authentication.selectors';
import {
    changePassword,
    changeUser,
    uploadProfilePicture,
} from '../../store/authentication/authentication.actions';
import {
    selectTwoFactorAuthRecoveryCodes,
    selectTwoFactorAuthSetupData,
    selectTwoFactorAuthSetupError,
    selectTwoFactorAuthSetupIsLoading,
} from 'account-hybrid/features/authentication/store/two-factor-auth-setup.selectors';
import { TwoFactorAuthSetupResponse } from 'account-hybrid/features/authentication/models/two-factor-auth-setup-response';
import {
    resetTwoFactorAuth,
    resetTwoFactorAuthRecoveryCodes,
    setupTwoFactorAuth,
    verifyTwoFactorAuth,
} from 'account-hybrid/features/authentication/store/two-factor-auth-setup/two-factor-auth-setup.actions';
import { CustomEditorComponent } from 'apps/account/src/account-shared/forms/editors/components/editors/custom-editor/custom-editor.component';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TwoFactorWizardStep } from 'account-hybrid/features/authentication/pages/two-factor-auth-wizard/two-factor-auth-wizard.component';
import { currentPasswordRefusal } from 'account-hybrid/features/authentication/store/two-factor-auth-setup/two-factor-auth-error';

// The profile calls fail with the server's code (INVALID_ARGUMENT, ...), not words, and an edit panel shows an
// error's message: without one, the panel shows its own text.
const withoutServerCode = (error: any) => error && {};

@UntilDestroy()
@Component({
    selector: 'ft-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false,
    // Eleven store-backed Observables rendered through `| async`, and nothing else this view reads
    // is written after construction. `closeButtonVisible` is assigned once in the constructor;
    // `files` is written by `(selected)="files = $event"`, an assignment in THIS template, so the
    // listener has already marked the view by the time it is read back by `(save)`. Every button
    // dispatches an NgRx action and the result returns through a selector — never a field.
    //
    // `twoFactorAuthStatusEditor` is a DEAD @ViewChild, not an imperative one: FTK-5347
    // (`dc215b1f81`) deleted the `#twoFactorAuthStatusEditor` element from this template, and no
    // code in the class ever reads the field. The query resolves to `undefined` forever, so there
    // is nothing here for a strategy change to affect. Safe to delete on its own ticket.
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileComponent {
    @ViewChild('twoFactorAuthStatusEditor')
    twoFactorAuthStatusEditor: CustomEditorComponent;
    userData$: Observable<UserData> = this.store.pipe(select(getUserData));

    changePasswordLoading$: Observable<boolean> = this.store.pipe(
        select(isChangePasswordLoading)
    );
    changeUserLoading$: Observable<boolean> = this.store.pipe(
        select(isChangeUserLoading)
    );
    uploadProfilePictureLoading$: Observable<boolean> = this.store.pipe(
        select(isUploadProfilePictureLoading)
    );

    // A refused current password is the one code the panel names; the others it shows as its own text
    changePasswordError$: Observable<any> = this.store.pipe(
        select(getChangePasswordError),
        map((error) => currentPasswordRefusal(error?.message) ? { message: currentPasswordRefusal(error.message) } : withoutServerCode(error))
    );
    changeUserError$: Observable<any> = this.store.pipe(
        select(getChangeUserError),
        map(withoutServerCode)
    );
    /** FT-9320: the one accepted-format list, rather than a copy pasted into the template. */
    accept = IMAGE_UPLOAD_ACCEPT;

    /**
     * FT-9047: ft-edit-panel renders `error?.message`, which on an HttpErrorResponse is Angular's
     * own "Http failure response for ...: 413 Request Entity Too Large" -- a developer string, and
     * the only thing this surface has ever shown. Mapped to the same sentences every other
     * uploader uses; the panel's `|| 'Error processing request'` fallback still covers a null.
     */
    uploadProfilePictureError$: Observable<{ message: string }> = this.store.pipe(
        select(getUploadProfilePictureError),
        map(error => error ? { message: imageUploadErrorMessage(error) } : null)
    );
    twoFactorSetupIsLoading$: Observable<boolean> = this.store.pipe(
        select(selectTwoFactorAuthSetupIsLoading)
    );
    twoFactorSetupData$: Observable<TwoFactorAuthSetupResponse> =
        this.store.pipe(select(selectTwoFactorAuthSetupData));
    twoFactorAuthRecoveryCodes$: Observable<string[]> = this.store.pipe(
        select(selectTwoFactorAuthRecoveryCodes)
    );
    twoFactorAuthError$: Observable<any> = this.store.pipe(
        select(selectTwoFactorAuthSetupError)
    );
    files: File[];

    twoFactorAuthWizardSteps = TwoFactorWizardStep;

    closeButtonVisible = false;

    constructor(
        public identityService: IdentityService,
        private stateService: StateService,
        private store: Store<AuthenticationState>
    ) {
        this.closeButtonVisible = !!this.getBackUrl();
    }

    goBack() {
        document.location.href = this.getBackUrl();
    }

    changeUser(profileData: Partial<UserData>) {
        this.store.dispatch(changeUser({ data: profileData }));
    }

    changePassword(data: ChangePasswordDto) {
        this.store.dispatch(changePassword({ data }));
    }

    uploadProfilePicture(files: File[]) {
        this.store.dispatch(uploadProfilePicture({ file: files[0] }));
    }

    getBackUrl() {
        const params = new URLSearchParams(this.stateService.params);
        return params.get('backUrl');
    }

    turnOffTwoFactorAuth(currentPassword?: string) {
        this.store.dispatch(
            setupTwoFactorAuth({
                is2FAEnabled: false,
                currentPassword,
            })
        );
    }

    turnOnTwoFactorAuth(currentPassword: string) {
        this.store.dispatch(
            setupTwoFactorAuth({
                is2FAEnabled: true,
                currentPassword,
            })
        );
    }

    resetTwoFactorAuthRecoveryCodes(currentPassword?: string) {
        this.store.dispatch(resetTwoFactorAuthRecoveryCodes({ currentPassword }));
    }

    onVerifyTwoFactorAuthOtp({ otp, currentPassword }: { otp: string; currentPassword?: string }) {
        this.store.dispatch(verifyTwoFactorAuth({ otp, currentPassword }));
    }

    onTwoFactorAuthWizardOpened(step: TwoFactorWizardStep) {
        if (step === TwoFactorWizardStep.Setup) {
            this.store.dispatch(
                setupTwoFactorAuth({
                    is2FAEnabled: true,
                })
            );
        }
    }

    onResetTwoFactorAuth(currentPassword?: string) {
        this.store.dispatch(resetTwoFactorAuth({ currentPassword }));
    }
}
